import { useState, useEffect, useCallback } from 'react';
import { db, auth, createAuditLog } from '../../../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { sendSystemNotification, buildEmailTemplate } from "../../../lib/notifications";
import { Application, AuditLog, Event } from '../../../types';
import { localToUTC, utcToLocalISO } from '../../../lib/static-data';
import { getSafeApplicantName } from '../../../lib/nameUtils';
import { safeFetch } from '../../../lib/api';
import { workspaceHeader } from '../../../lib/workspaces';
import { notifyToast } from '../../../lib/toastStore';
import { confirmDialog } from '../../../lib/confirmStore';

export function useEvents(options: {
  adminEmail: string;
  applications: Application[];
  setApplications: (apps: Application[]) => void;
  /** When false the Events dataset is never auto-fetched (module is No Access). */
  enabled?: boolean;
}) {
  const { adminEmail, applications, setApplications } = options;
  const bootstrapEnabled = options.enabled !== false;

  const [events, setEvents] = useState<Event[]>([]);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [isEditingEventId, setIsEditingEventId] = useState<string | null>(null);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const eventPageSize = 20;

  // Event view mode
  const [eventViewMode, setEventViewMode] = useState<'calendar' | 'list'>('list');
  const [calendarViewType, setCalendarViewType] = useState<'month' | 'week' | 'day'>('month');
  const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [filterEventType, setFilterEventType] = useState<string>('All');
  const [filterEventStatus, setFilterEventStatus] = useState<string>('All');
  const [filterEventApplicant, setFilterEventApplicant] = useState<string>('All');
  const [filterDateStart, setFilterDateStart] = useState<string>('');
  const [filterDateEnd, setFilterDateEnd] = useState<string>('');
  const [filterDatePreset, setFilterDatePreset] = useState<string>('All Time');
  const [focusedEvent, setFocusedEvent] = useState<Event | null>(null);

  // Event form state
  const [eventTitle, setEventTitle] = useState('');
  const [eventType, setEventType] = useState<'Embassy' | 'Interview' | 'Orientation' | 'Training' | 'Flight' | 'Medical' | 'Other'>('Embassy');
  const [eventDateTime, setEventDateTime] = useState('');
  const [eventTimezone, setEventTimezone] = useState('America/New_York');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventInternalNotes, setEventInternalNotes] = useState('');
  const [eventExternalNotes, setEventExternalNotes] = useState('');
  const [eventStatus, setEventStatus] = useState<string>('Scheduled');
  const [eventApplicantId, setEventApplicantId] = useState('');
  const [eventNotifyApplicants, setEventNotifyApplicants] = useState(true);
  // Virtual meeting state
  const [isVirtualMeeting, setIsVirtualMeeting] = useState(false);
  const [eventAssignedTo, setEventAssignedTo] = useState('');
  const [eventAssignedToName, setEventAssignedToName] = useState('');
  // Multi-agent assignment
  const [agentsList, setAgentsList] = useState<{ uid: string; name: string; email: string; role?: string }[]>([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentsError, setAgentsError] = useState(false);

  const fetchEvents = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const data = await safeFetch('/api/admin/events');
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (err) {
      console.warn("Could not fetch events:", err);
    }
  }, []);

  // Fetch through the server Admin SDK instead of subscribing directly to the
  // Firestore collection. Workspace admins and custom team roles are authorized
  // by server-side workspace permissions even when their Firebase token does not
  // carry the legacy global admin claim required by Firestore rules.
  useEffect(() => {
    if (!bootstrapEnabled) return undefined;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        void fetchEvents();
      }
    });
    return () => unsubscribe();
  }, [fetchEvents, bootstrapEnabled]);

  // Fetch agents (admins + referrers + employers with enhanced permission)
  const fetchAgents = useCallback(async () => {
    setAgentsError(false);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) {
        setAgentsLoaded(true);
        return;
      }
      // 12s timeout so a slow/hung response can never leave the UI stuck on
      // "Loading agents..." forever — it falls through to the error/retry state.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const res = await fetch('/api/admin/agents', {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          setAgentsList(Array.isArray(data.agents) ? data.agents : []);
        } else {
          setAgentsError(true);
          console.warn('fetchAgents returned', res.status);
        }
      } finally {
        clearTimeout(timer);
      }
    } catch (err) {
      console.warn('Could not fetch agents list:', err);
      setAgentsError(true);
    } finally {
      setAgentsLoaded(true);
    }
  }, []);

  // Filter events based on search and filters
  const filteredEvents = useCallback(() => {
    return events.filter(ev => {
      if (eventSearchQuery) {
        const q = eventSearchQuery.toLowerCase();
        const searchable = `${ev.title} ${ev.location || ''} ${ev.description || ''}`.toLowerCase();
        if (!searchable.includes(q)) {
          // Also check applicant name if we have applications
          const app = applications.find(a => a.applicationId === ev.applicantId);
          if (!app) return false;
          const name = `${getSafeApplicantName(app)} ${app.userEmail || ''}`.toLowerCase();
          if (!name.includes(q)) return false;
        }
      }
      if (filterEventStatus !== 'Archived' && (ev.archived === true || ev.status === 'Archived')) return false;
      if (filterEventType !== 'All' && ev.eventType !== filterEventType) return false;
      if (filterEventStatus !== 'All' && ev.status !== filterEventStatus) return false;
      if (filterEventApplicant !== 'All' && ev.applicantId !== filterEventApplicant) return false;
      if (filterDateStart && ev.dateTime && new Date(ev.dateTime) < new Date(filterDateStart)) return false;
      if (filterDateEnd && ev.dateTime && new Date(ev.dateTime) > new Date(filterDateEnd + 'T23:59:59')) return false;
      return true;
    });
  }, [events, eventSearchQuery, filterEventType, filterEventStatus, filterEventApplicant, filterDateStart, filterDateEnd, applications]);

  const getFilteredEvents = filteredEvents;

  const handleMoveEventDate = async (eventId: string, newDate: Date) => {
    try {
      await updateDoc(doc(db, 'events', eventId), {
        dateTime: newDate.toISOString(),
        updatedAt: new Date().toISOString()
      });
      setEvents(prev => prev.map(ev => ev.eventId === eventId ? { ...ev, dateTime: newDate.toISOString() } : ev));
    } catch (err) {
      console.error("Failed to move event date:", err);
    }
  };

  // PT7-001: Now using shared createAuditLog from firebase.ts (server-side write)

  const handleSaveEvent = async () => {
    if (isSavingEvent) return;
    setIsSavingEvent(true);
    if (isCreatingEvent) {
      if (!eventTitle.trim() || !eventDateTime) {
        notifyToast('warning', 'Please fill in the title and date/time.');
        setIsSavingEvent(false);
        return;
      }
      if (!isVirtualMeeting && !eventLocation.trim()) {
        notifyToast('warning', 'Please provide a location or enable Virtual Meeting.');
        setIsSavingEvent(false);
        return;
      }
      try {
        const matchingApp = applications.find(a => a.applicationId === eventApplicantId);

        // Routed through the server Admin SDK — the server generates the Jitsi
        // room ONCE on create and (on edits) preserves the permanent room.
        const payload: any = {
          title: eventTitle.trim(),
          eventType,
          dateTime: localToUTC(eventDateTime, eventTimezone),
          timezone: eventTimezone,
          location: eventLocation.trim(),
          description: eventDescription.trim() || eventExternalNotes.trim() || '',
          externalNotes: eventExternalNotes.trim() || eventDescription.trim() || '',
          internalNotes: eventInternalNotes.trim() || '',
          status: eventStatus,
          applicantId: eventApplicantId,
          isVirtual: isVirtualMeeting,
          locationType: isVirtualMeeting ? 'jitsi' : 'url',
        };
        if (eventAssignedTo) {
          payload.assignedTo = eventAssignedTo;
          payload.assignedToName = eventAssignedToName;
        }
        const token = await auth.currentUser?.getIdToken(true);
        const res = await fetch('/api/admin/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to save event');
        const eventId = data.eventId;
        await createAuditLog('CREATE_EVENT', eventTitle.trim(), `Created event: ${eventTitle.trim()} on ${new Date(eventDateTime).toLocaleDateString()}`);
        await fetchEvents();
        setIsCreatingEvent(false);
        notifyToast('success', `Event "${eventTitle.trim()}" created successfully.`);

        // E-1 / V-5: Auto-notify linked applicants when event created
        if (eventApplicantId) {
          const app = applications.find(a => a.applicationId === eventApplicantId);
          if (app) {
            const { emailEventScheduled, emailEmbassyInterviewScheduled } = await import('../../../lib/emailTriggers');
            if (eventType === 'Embassy' as any) {
              const dateStr = new Date(eventDateTime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
              await emailEmbassyInterviewScheduled(app, eventTitle.trim(), dateStr);
            } else {
              await emailEventScheduled(app, eventTitle.trim());
            }
            try {
              await createAuditLog('NOTIFY_EVENT', eventApplicantId, `Notification sent for new event: ${eventTitle.trim()}`);
            } catch {}
          }
        }
      } catch (err: any) {
        console.error("Failed to save event:", err);
        notifyToast('error', err.message || 'Failed to save event.');
      } finally {
        setIsSavingEvent(false);
      }
    } else if (isEditingEventId) {
      try {
        // Routed through the server Admin SDK — editing NEVER regenerates the
        // Jitsi room unless the destination type is intentionally changed
        // (the server preserves the existing meetingUrl for virtual events).
        const payload: any = {
          eventId: isEditingEventId,
          title: eventTitle.trim(),
          eventType,
          dateTime: localToUTC(eventDateTime, eventTimezone),
          timezone: eventTimezone,
          location: eventLocation.trim(),
          description: eventDescription.trim() || eventExternalNotes.trim() || '',
          externalNotes: eventExternalNotes.trim() || eventDescription.trim() || '',
          internalNotes: eventInternalNotes.trim() || '',
          status: eventStatus,
          applicantId: eventApplicantId,
          isVirtual: isVirtualMeeting,
          locationType: isVirtualMeeting ? 'jitsi' : 'url',
        };
        if (eventAssignedTo) {
          payload.assignedTo = eventAssignedTo;
          payload.assignedToName = eventAssignedToName;
        }
        const token = await auth.currentUser?.getIdToken(true);
        const res = await fetch('/api/admin/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to update event');
        await createAuditLog('EDIT_EVENT', isEditingEventId, `Edited event: ${eventTitle.trim()}`);
        await fetchEvents();
        setIsEditingEventId(null);
        notifyToast('success', `Event "${eventTitle.trim()}" updated successfully.`);

        // E-2: Notify applicant when event is updated
        if (eventApplicantId) {
          const app = applications.find(a => a.applicationId === eventApplicantId);
          if (app) {
            const { emailEventUpdated } = await import('../../../lib/emailTriggers');
            await emailEventUpdated(app, eventTitle.trim());
          }
        }

        // Prompt admin to notify linked applicants of update
        if (eventNotifyApplicants && eventApplicantId) {
          if (await confirmDialog('Send notification to linked applicants?')) {
            const app = applications.find(a => a.applicationId === eventApplicantId);
            if (app) {
              const appAny = app as any;
              const applicantName = getSafeApplicantName(appAny);
              const applicantEmail = appAny.userEmail || appAny.personalDetails?.email || '';
              if (applicantEmail) {
                const dateStr = new Date(eventDateTime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                const timeStr = new Date(eventDateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
                await sendSystemNotification({
                  to: applicantEmail,
                  toName: applicantName,
                  subject: `Event Updated – ${eventTitle.trim()}`,
                  htmlBody: buildEmailTemplate({
                    applicantName,
                    title: 'Event Updated',
                    bodyHtml: `<p>An event has been <strong>updated</strong>:</p><div class="highlight"><p style="font-weight:700;margin:0 0 4px 0;">${eventTitle.trim()}</p><p style="font-size:12px;color:#64748b;margin:0;"><strong>Date:</strong> ${dateStr}</p><p style="font-size:12px;color:#64748b;margin:4px 0 0 0;"><strong>Time:</strong> ${timeStr}</p><p style="font-size:12px;color:#64748b;margin:4px 0 0 0;"><strong>Location:</strong> ${eventLocation.trim()}</p>${eventDescription.trim() ? `<p style="font-size:12px;color:#64748b;margin:4px 0 0 0;"><strong>Details:</strong> ${eventDescription.trim()}</p>` : ''}</div><p>Please check your portal for the latest information.</p>`,
                    portalUrl: (window.__FRONTEND_URL || 'https://portal.isats.org') + '/applicant',
                    ctaText: 'View My Events',
                  }),
                  notificationType: 'event_updated',
                  applicationId: eventApplicantId,
                });
                // Log notification in activity log
                try {
                  await createAuditLog('NOTIFY_EVENT_UPDATE', eventApplicantId, `Update notification sent for event: ${eventTitle.trim()}`);
                } catch {}
              }
            }
          }
        }
      } catch (err: any) {
        console.error("Failed to update event:", err);
        notifyToast('error', err.message || 'Failed to update event.');
      } finally {
        setIsSavingEvent(false);
      }
    } else {
      setIsSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId: string, eventTitle?: string) => {
    const targetEv = events.find(e => e.eventId === eventId || (e as any).id === eventId);
    const titleToDisplay = eventTitle || targetEv?.title || 'Event';
    if (!(await confirmDialog(`Are you sure you want to delete "${titleToDisplay}"? This cannot be undone.`))) return;

    let shouldNotify = false;
    const isGroupEvent = targetEv?.isGroupEvent || targetEv?.eventType === 'Group Event';
    if (isGroupEvent) {
      shouldNotify = await confirmDialog(
        'Send a cancellation email notice to all booked attendees and invited applicants? ' +
        'Choose "Send notifications" to email them, or "Delete without notifying" to delete WITHOUT sending notifications.',
        { confirmLabel: 'Send notifications', cancelLabel: 'Delete without notifying' },
      );
    }

    try {
      // Route through the server Admin SDK — bypasses Firestore security rules
      // that gate `deleteDoc` on `request.auth.token.admin == true`. A stale or
      // missing admin claim on the client token otherwise produces
      // "Missing or insufficient permissions".
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) { console.warn("No auth token available for delete"); return; }
      const res = await fetch(`/api/admin/events/${encodeURIComponent(eventId)}?notify=${shouldNotify}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setEvents(prev => prev.filter(ev => ev.eventId !== eventId && (ev as any).id !== eventId));
      await createAuditLog('DELETE_EVENT', eventId, `Deleted event ${eventId}`);
      await fetchEvents();
      notifyToast('success', `Event "${titleToDisplay}" deleted successfully.`);
    } catch (err: any) {
      console.error("Failed to delete event:", err);
      notifyToast('error', err.message || "Failed to delete event. Please try again.");
    }
  };

  const handleEditEventClick = (ev: Event) => {
    setIsEditingEventId(ev.eventId);
    setEventTitle(ev.title);
    setEventType(ev.eventType as any);
    const targetTz = ev.timezone || 'America/New_York';
    let formattedDateTime = '';
    if (ev.dateTime) {
      formattedDateTime = utcToLocalISO(ev.dateTime, targetTz);
    }
    setEventDateTime(formattedDateTime);
    setEventTimezone(targetTz);
    setEventLocation(ev.location || '');
    setEventDescription(ev.description || '');
    setEventInternalNotes(ev.internalNotes || '');
    setEventExternalNotes(ev.externalNotes || ev.description || '');
    setEventStatus(ev.status as any);
    setEventApplicantId(ev.applicantId || '');
    setIsVirtualMeeting(ev.isVirtual || false);
    setEventAssignedTo(ev.assignedTo || '');
    setEventAssignedToName(ev.assignedToName || '');
  };

  const eventsTotalPages = Math.ceil(getFilteredEvents().length / eventPageSize);
  const displayedEvents = getFilteredEvents().slice((eventsPage - 1) * eventPageSize, eventsPage * eventPageSize);

  return {
    events,
    setEvents,
    isCreatingEvent,
    setIsCreatingEvent,
    isEditingEventId,
    setIsEditingEventId,
    isSavingEvent,
    eventViewMode,
    setEventViewMode,
    calendarViewType,
    setCalendarViewType,
    currentCalendarDate,
    setCurrentCalendarDate,
    eventSearchQuery,
    setEventSearchQuery,
    filterEventType,
    setFilterEventType,
    filterEventStatus,
    setFilterEventStatus,
    filterEventApplicant,
    setFilterEventApplicant,
    filterDateStart,
    setFilterDateStart,
    filterDateEnd,
    setFilterDateEnd,
    filterDatePreset,
    setFilterDatePreset,
    focusedEvent,
    setFocusedEvent,
    eventTitle,
    setEventTitle,
    eventType,
    setEventType,
    eventDateTime,
    setEventDateTime,
    eventTimezone,
    setEventTimezone,
    eventLocation,
    setEventLocation,
    eventDescription,
    setEventDescription,
    eventInternalNotes,
    setEventInternalNotes,
    eventExternalNotes,
    setEventExternalNotes,
    eventStatus,
    setEventStatus,
    eventApplicantId,
    setEventApplicantId,
    eventNotifyApplicants,
    setEventNotifyApplicants,
    isVirtualMeeting,
    setIsVirtualMeeting,
    eventAssignedTo,
    setEventAssignedTo,
    eventAssignedToName,
    setEventAssignedToName,
    agentsList,
    agentsLoaded,
    agentsError,
    fetchAgents,
    getFilteredEvents,
    eventsPage,
    setEventsPage,
    eventsTotalPages,
    displayedEvents,
    fetchEvents,
    handleMoveEventDate,
    handleSaveEvent,
    handleDeleteEvent,
    handleEditEventClick,
    createAuditLog,
  };
}
